import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  BookUser, Search, Star, Plus, MapPin, Phone, Mail, LayoutGrid, List,
  Loader2, SlidersHorizontal, Settings2, Building, Clapperboard, X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  getDirectoryCategories, getDirectoryTags, getDirectoryContacts,
  getDirectoryFacets, toggleDirectoryFavorite,
} from "../services/api";
import { ContactFormDialog } from "../components/directory/ContactFormDialog";
import { ContactDetailDialog } from "../components/directory/ContactDetailDialog";
import { DirectoryManagerDialog } from "../components/directory/DirectoryManagerDialog";
import { displayName, initials } from "../components/directory/directoryUtils";
import { useAuth } from "../context/AuthContext";

const ALL = "__all__";

const FavStar = ({ contact, onToggled }) => (
  <button
    className={`p-1.5 rounded-md transition-colors ${contact.is_favorite ? "text-amber-500" : "text-slate-300 hover:text-amber-500"}`}
    onClick={async (e) => {
      e.stopPropagation();
      const res = await toggleDirectoryFavorite(contact.id);
      onToggled(contact.id, res.data.is_favorite);
    }}
    data-testid={`fav-btn-${contact.id}`}
  >
    <Star size={15} fill={contact.is_favorite ? "currentColor" : "none"} />
  </button>
);

const ContactCard = ({ c, cat, onOpen, onFavToggled }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 hover:border-[#26788A]/40 hover:shadow-sm transition-all cursor-pointer"
    onClick={() => onOpen(c)} data-testid={`contact-card-${c.id}`}>
    <div className="flex items-start gap-3">
      {c.photo ? (
        <img src={c.photo} alt="" className="w-12 h-12 rounded-xl object-cover border border-slate-100" />
      ) : (
        <div className="w-12 h-12 rounded-xl bg-[#26788A]/10 flex items-center justify-center shrink-0">
          {c.kind === "place" ? <Building size={18} className="text-[#26788A]" /> :
            <span className="text-sm font-bold text-[#26788A]">{initials(c)}</span>}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900 truncate">{displayName(c)}</p>
        <p className="text-xs text-slate-500 truncate">
          {c.kind === "place" ? (c.place_type || "Lieu") : (c.job || cat?.name || "—")}
        </p>
        {c.city && <p className="text-[11px] text-slate-400 flex items-center gap-0.5 mt-0.5"><MapPin size={10} /> {c.city}</p>}
      </div>
      <FavStar contact={c} onToggled={onFavToggled} />
    </div>
    <div className="flex items-center gap-2 mt-3 flex-wrap">
      {c.phone && (
        <a href={`tel:${c.phone.replace(/\s/g, "")}`} onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-1 text-[11px] font-medium text-[#26788A] bg-[#26788A]/8 bg-teal-50 px-2 py-1 rounded-md hover:bg-teal-100" data-testid={`card-phone-${c.id}`}>
          <Phone size={11} /> {c.phone}
        </a>
      )}
      {c.email && (
        <a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-1 text-[11px] font-medium text-slate-500 bg-slate-50 px-2 py-1 rounded-md hover:bg-slate-100 truncate max-w-[160px]">
          <Mail size={11} className="shrink-0" /> <span className="truncate">{c.email}</span>
        </a>
      )}
      {c.projects_count > 0 && (
        <span className="flex items-center gap-1 text-[10px] font-semibold text-violet-600 bg-violet-50 px-2 py-1 rounded-md ml-auto" title="Projets avec Studio Raneem">
          <Clapperboard size={10} /> {c.projects_count}
        </span>
      )}
    </div>
  </div>
);

const ContactRow = ({ c, cat, onOpen, onFavToggled }) => (
  <div className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-100 last:border-b-0 hover:bg-slate-50/70 cursor-pointer"
    onClick={() => onOpen(c)} data-testid={`contact-row-${c.id}`}>
    {c.photo ? (
      <img src={c.photo} alt="" className="w-8 h-8 rounded-lg object-cover" />
    ) : (
      <div className="w-8 h-8 rounded-lg bg-[#26788A]/10 flex items-center justify-center shrink-0">
        {c.kind === "place" ? <Building size={13} className="text-[#26788A]" /> :
          <span className="text-[10px] font-bold text-[#26788A]">{initials(c)}</span>}
      </div>
    )}
    <span className="text-sm font-medium text-slate-800 w-48 truncate shrink-0">{displayName(c)}</span>
    <span className="text-xs text-slate-500 w-40 truncate shrink-0 hidden md:block">{c.kind === "place" ? (c.place_type || "Lieu") : (c.job || "—")}</span>
    <span className="text-xs text-slate-400 w-28 truncate shrink-0 hidden lg:block">{cat?.name || "—"}</span>
    <span className="text-xs text-slate-400 w-24 truncate shrink-0 hidden sm:block">{c.city || "—"}</span>
    {c.phone ? (
      <a href={`tel:${c.phone.replace(/\s/g, "")}`} onClick={(e) => e.stopPropagation()} className="text-xs text-[#26788A] hover:underline w-32 truncate shrink-0">{c.phone}</a>
    ) : <span className="w-32 shrink-0"></span>}
    <span className="flex-1"></span>
    {c.projects_count > 0 && (
      <span className="flex items-center gap-1 text-[10px] font-semibold text-violet-600"><Clapperboard size={10} /> {c.projects_count}</span>
    )}
    <FavStar contact={c} onToggled={onFavToggled} />
  </div>
);

export default function DirectoryPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [facets, setFacets] = useState({ cities: [], jobs: [] });
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [catFilter, setCatFilter] = useState(ALL);
  const [cityFilter, setCityFilter] = useState(ALL);
  const [jobFilter, setJobFilter] = useState(ALL);
  const [tagFilter, setTagFilter] = useState(ALL);
  const [favOnly, setFavOnly] = useState(false);
  const [availOnly, setAvailOnly] = useState(false);
  const [projOnly, setProjOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState("cards");
  const [formDialog, setFormDialog] = useState(null); // {contact?, kind}
  const [detailId, setDetailId] = useState(null);
  const [managerOpen, setManagerOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const loadMeta = useCallback(() => {
    getDirectoryCategories().then((r) => setCategories(r.data)).catch(() => {});
    getDirectoryTags().then((r) => setTags(r.data)).catch(() => {});
    getDirectoryFacets().then((r) => setFacets(r.data)).catch(() => {});
  }, []);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (debouncedQ.trim()) params.q = debouncedQ.trim();
      if (catFilter !== ALL) params.category_id = catFilter;
      if (cityFilter !== ALL) params.city = cityFilter;
      if (jobFilter !== ALL) params.job = jobFilter;
      if (tagFilter !== ALL) params.tag = tagFilter;
      if (favOnly) params.favorites = true;
      if (availOnly) params.available = true;
      if (projOnly) params.has_projects = true;
      const res = await getDirectoryContacts(params);
      setContacts(res.data);
    } catch {
      toast.error("Erreur de chargement du répertoire");
    } finally { setLoading(false); }
  }, [debouncedQ, catFilter, cityFilter, jobFilter, tagFilter, favOnly, availOnly, projOnly]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadContacts(); }, [loadContacts]);

  const catById = (id) => categories.find((c) => c.id === id);
  const onFavToggled = (id, val) => setContacts((prev) => prev.map((c) => c.id === id ? { ...c, is_favorite: val } : c));
  const refreshAll = () => { loadContacts(); loadMeta(); };

  const activeFiltersCount = [catFilter, cityFilter, jobFilter, tagFilter].filter((f) => f !== ALL).length + (availOnly ? 1 : 0) + (projOnly ? 1 : 0);
  const clearFilters = () => { setCatFilter(ALL); setCityFilter(ALL); setJobFilter(ALL); setTagFilter(ALL); setAvailOnly(false); setProjOnly(false); };

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto overflow-y-auto h-screen pb-24" data-testid="directory-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <BookUser size={24} className="text-[#26788A]" /> Répertoire
          <span className="text-sm font-normal text-slate-400">{contacts.length} fiche{contacts.length > 1 ? "s" : ""}</span>
        </h1>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={() => setManagerOpen(true)} data-testid="manage-directory-btn">
              <Settings2 size={14} className="mr-1.5" /> Catégories & tags
            </Button>
          )}
          <Button size="sm" variant="outline" className="text-[#26788A] border-[#26788A]/30" onClick={() => setFormDialog({ kind: "place" })} data-testid="add-place-btn">
            <Plus size={14} className="mr-1" /> Ajouter un lieu
          </Button>
          <Button size="sm" className="bg-[#26788A] hover:bg-[#1D5E6C] text-white" onClick={() => setFormDialog({ kind: "person" })} data-testid="add-contact-btn">
            <Plus size={14} className="mr-1" /> Ajouter un contact
          </Button>
        </div>
      </div>

      {/* Recherche + actions */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher : maquilleuse Lille, figurant, appartement tournage…"
            className="pl-9 h-10" data-testid="directory-search-input" />
          {q && (
            <button className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500" onClick={() => setQ("")}>
              <X size={14} />
            </button>
          )}
        </div>
        <Button size="sm" variant={favOnly ? "default" : "outline"}
          className={`h-10 ${favOnly ? "bg-amber-500 hover:bg-amber-600 text-white" : "text-amber-600 border-amber-200"}`}
          onClick={() => setFavOnly(!favOnly)} data-testid="favorites-filter-btn">
          <Star size={14} className="mr-1.5" fill={favOnly ? "currentColor" : "none"} /> Favoris
        </Button>
        <Button size="sm" variant="outline" className={`h-10 ${activeFiltersCount ? "border-[#26788A] text-[#26788A]" : ""}`}
          onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters-btn">
          <SlidersHorizontal size={14} className="mr-1.5" /> Filtres{activeFiltersCount > 0 ? ` (${activeFiltersCount})` : ""}
        </Button>
        <div className="flex bg-slate-100 rounded-lg p-0.5">
          <button className={`p-2 rounded-md ${viewMode === "cards" ? "bg-white shadow-sm text-[#26788A]" : "text-slate-400"}`} onClick={() => setViewMode("cards")} data-testid="view-cards-btn"><LayoutGrid size={15} /></button>
          <button className={`p-2 rounded-md ${viewMode === "list" ? "bg-white shadow-sm text-[#26788A]" : "text-slate-400"}`} onClick={() => setViewMode("list")} data-testid="view-list-btn"><List size={15} /></button>
        </div>
      </div>

      {/* Filtres */}
      {showFilters && (
        <div className="flex flex-wrap items-center gap-2 mb-4 bg-white border border-slate-200 rounded-xl p-3" data-testid="filters-panel">
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="w-44 h-9 text-xs" data-testid="filter-category"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-[260px]">
              <SelectItem value={ALL}>Toutes catégories</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.contacts_count})</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={jobFilter} onValueChange={setJobFilter}>
            <SelectTrigger className="w-40 h-9 text-xs" data-testid="filter-job"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-[260px]">
              <SelectItem value={ALL}>Tous métiers</SelectItem>
              {facets.jobs.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={cityFilter} onValueChange={setCityFilter}>
            <SelectTrigger className="w-36 h-9 text-xs" data-testid="filter-city"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-[260px]">
              <SelectItem value={ALL}>Toutes villes</SelectItem>
              {facets.cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={tagFilter} onValueChange={setTagFilter}>
            <SelectTrigger className="w-36 h-9 text-xs" data-testid="filter-tag"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-[260px]">
              <SelectItem value={ALL}>Tous tags</SelectItem>
              {tags.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <button className={`text-xs px-3 py-2 rounded-lg border transition-colors ${availOnly ? "bg-emerald-50 border-emerald-300 text-emerald-700 font-medium" : "border-slate-200 text-slate-500"}`}
            onClick={() => setAvailOnly(!availOnly)} data-testid="filter-available">Disponibilité renseignée</button>
          <button className={`text-xs px-3 py-2 rounded-lg border transition-colors ${projOnly ? "bg-violet-50 border-violet-300 text-violet-700 font-medium" : "border-slate-200 text-slate-500"}`}
            onClick={() => setProjOnly(!projOnly)} data-testid="filter-has-projects">A déjà travaillé avec nous</button>
          {activeFiltersCount > 0 && (
            <button className="text-xs text-red-400 hover:text-red-500 ml-auto" onClick={clearFilters} data-testid="clear-filters-btn">Réinitialiser</button>
          )}
        </div>
      )}

      {/* Résultats */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={26} className="animate-spin text-slate-300" /></div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-16 bg-white border border-dashed border-slate-200 rounded-xl">
          <BookUser size={30} className="mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500 text-sm">{q || activeFiltersCount || favOnly ? "Aucune fiche ne correspond à votre recherche." : "Le répertoire est vide — ajoutez votre premier contact ou lieu."}</p>
        </div>
      ) : viewMode === "cards" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="contacts-grid">
          {contacts.map((c) => (
            <ContactCard key={c.id} c={c} cat={catById(c.category_id)} onOpen={(x) => setDetailId(x.id)} onFavToggled={onFavToggled} />
          ))}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="contacts-list">
          {contacts.map((c) => (
            <ContactRow key={c.id} c={c} cat={catById(c.category_id)} onOpen={(x) => setDetailId(x.id)} onFavToggled={onFavToggled} />
          ))}
        </div>
      )}

      <ContactFormDialog
        open={!!formDialog}
        onClose={() => setFormDialog(null)}
        contact={formDialog?.contact}
        kind={formDialog?.kind}
        categories={categories}
        tags={tags}
        onSaved={refreshAll}
        onTagsChanged={loadMeta}
      />
      <ContactDetailDialog
        open={!!detailId}
        onClose={() => setDetailId(null)}
        contactId={detailId}
        categories={categories}
        onEdit={(c) => { setDetailId(null); setFormDialog({ contact: c, kind: c.kind }); }}
        onChanged={refreshAll}
      />
      <DirectoryManagerDialog open={managerOpen} onClose={() => setManagerOpen(false)} categories={categories} tags={tags} onChanged={loadMeta} />
    </div>
  );
}
